import { z } from "zod";

import { topLevelQuerySpecSchema } from "../differential";
import { type JsonPathLiteral } from "../differential/jsonTable/JsonPathLiteral";
import { sqliteColumnDefSchema } from "../differential/schema";
import { pageMetadata } from "./pageMetadata";

const publishSettings = z.object({
	template: z.union([z.literal("blog"), z.literal("tech-docs-vitepress")]).optional(),
	logo: z
		.object({
			dark: z.string().optional(),
			light: z.string().optional(),
		})
		.optional(),
	favicon: z.string().optional(),
	urlPrefix: z.string().optional(),
	topbarLinks: z
		.array(
			z
				.object({
					name: z.string(),
					url: z.string(),
				})
				.nullish()
		)
		.optional(),
	topbarCtaButton: z
		.object({
			name: z.string(),
			url: z.string(),
		})
		.optional(),
	footerSocials: z
		.object({
			x: z.string().optional(),
			bluesky: z.string().optional(),
			github: z.string().optional(),
			linkedin: z.string().optional(),
		})
		.optional(),
});

export const tablePermissionSchema = z.object({
	subject: z.union([
		z.custom<`user:${string}`>((val) => typeof val === "string" && val.startsWith("user:")),
		z.custom<`group:${string}`>((val) => typeof val === "string" && val.startsWith("group:")),
	]),
	is: z.enum(["reader_all", "reader_own", "writer_all", "writer_own"]),
});

const viewColumnDefBase = z.object({
	accessorKey: z.custom<JsonPathLiteral>((val) => typeof val === "string"),
	header: z.string().optional(),
	primaryDisplayColumn: z.boolean().optional(),
	placementHint: z.enum(["top-right", "bottom-right", "bottom-left"]).optional(),
	visibleOnCard: z.boolean().optional(),
	readonly: z.boolean().optional(),
});

export const personDefaultValues = ["Empty", "Current user"] as const;
const personDefaultValuesSchema = z.enum(personDefaultValues);

const viewColumnDefText = viewColumnDefBase.extend({
	type: z.literal("Text"),
	typeInfo: z
		.object({
			defaultValue: z.string().optional(),
		})
		.optional(),
});

const viewColumnDefReaction = viewColumnDefBase.extend({
	type: z.literal("Reaction"),
});

const colorVariantOptions = z.union([
	z.literal("default"), // gray
	z.literal("red"),
	z.literal("yellow"),
	z.literal("green"),
	z.literal("cyan"),
	z.literal("blue"),
	z.literal("purple"),
	z.literal("pink"),
]);

const viewColumnDefSelect = viewColumnDefBase.extend({
	type: z.literal("Select"),
	typeInfo: z
		.object({
			options: z.array(z.object({ label: z.string(), color: colorVariantOptions })),
			defaultValue: z.string().optional(),
		})
		.optional(),
});
export type ViewColumnDefSelect = z.infer<typeof viewColumnDefSelect>;

const viewColumnDefPerson = viewColumnDefBase.extend({
	type: z.literal("Person"),
	typeInfo: z
		.object({
			defaultValue: personDefaultValuesSchema.optional(),
		})
		.optional(),
});

const viewColumnDef = z.discriminatedUnion("type", [
	viewColumnDefText,
	viewColumnDefReaction,
	viewColumnDefSelect,
	viewColumnDefPerson,
]);
export type ViewColumnDef = z.infer<typeof viewColumnDef>;

export const viewSpecSchema = z.object({
	viewTitle: z.string().optional(),
	query: topLevelQuerySpecSchema,
	columns: z.array(viewColumnDef),
});

export const baseSpecSchema = z.object({
	tables: z.record(
		z.object({
			permissions: z.array(tablePermissionSchema),
			columns: z.array(sqliteColumnDefSchema),
		})
	),
	views: z.record(viewSpecSchema),
});

export type PublishSettings = z.infer<typeof publishSettings>;
export type TablePermission = z.infer<typeof tablePermissionSchema>;
export type ViewSpec = z.infer<typeof viewSpecSchema>;
export type BaseSpec = z.infer<typeof baseSpecSchema>;

export const makeUrlPrefix = (
	workspaceName: string,
	documentId: string,
	publishSettings: PublishSettings | undefined
) => {
	const defaultUrlPrefix = `/d/${workspaceName}/${documentId}`;

	const urlPrefix = publishSettings?.urlPrefix;
	if (!urlPrefix) {
		return defaultUrlPrefix;
	}

	return urlPrefix;
};

/**
 * A direct mirror of the GraphQL `CanvasMetadata` type
 */
export const DocumentMetadataSchema = z.object({
	id: z.string(),
	schemaVersion: z.number().default(0),

	title: z.string(),
	prettyName: z.string().optional(),
	created: z.coerce.date().default(new Date()),

	pagesMetadata: z.array(pageMetadata),

	publishSettings: publishSettings.optional(),

	base: baseSpecSchema.optional(),
});

export const messageIdempotentInitMetadataRoomResponseAccepted = z.object({
	type: z.literal("idempotent-init-metadata-room-response-accepted"),
	version: z.number(),
	canvasMetadata: DocumentMetadataSchema,
	page: z.unknown(),
});

export type MessageInitMetadataRoomResponseAccepted = z.infer<
	typeof messageIdempotentInitMetadataRoomResponseAccepted
>;

export const messageIdempotentInitMetadataRoomResponseRejected = z.object({
	type: z.literal("idempotent-init-metadata-room-response-rejected"),
	version: z.number(),
});

export type MessageInitMetadataRoomResponseRejected = z.infer<
	typeof messageIdempotentInitMetadataRoomResponseRejected
>;

/**
 * A direct mirror of the GraphQL `CanvasMetadata` type.
 */
export type DocumentMetadata = z.infer<typeof DocumentMetadataSchema>;

export type DocumentPageMetadata = DocumentMetadata["pagesMetadata"][number];
